import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { Mention } from '../../../domain/mention';
import { MentionStoreService } from '../../../services/stores/mention-store.service';
import { Router } from '@angular/router';
import { Property } from '../../../domain/property';
import { PropertyStoreService } from '../../../services/stores/property-store.service';
import { LoadingOverlayStoreService } from '../../../services/stores/loading-overlay-store.service';
import { LoadingState } from '../../../domain/loading-state';
import { TaskUtils } from '../../../util/task-utils';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { DatePropertyTimezonePipe } from '../../../pipes/date-property-timezone.pipe';
import { WorkflowType } from '../../../domain/enums/workflow-type';
import { PhobosTaskInstanceService } from '../../../services/phobos-task-instance.service';
import { finalize, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
   selector: 'app-my-mentions',
   standalone: true,
   imports: [
      CommonModule,
      TableModule,
      ProgressSpinnerModule,
      ButtonModule,
      CheckboxModule,
      ToastModule,
      DatePropertyTimezonePipe
   ],
   templateUrl: './my-mentions.component.html',
   styleUrls: ['./my-mentions.component.scss']
})
export class MyMentionsComponent implements OnInit {
   mentions: Mention[] = [];
   selectedMentions: Mention[] = [];
   isLoading: boolean = false;
   isRemovingMentions: boolean = false;
   private selectedProperty: Property | undefined;

   constructor(
      private mentionStore: MentionStoreService,
      private propStore: PropertyStoreService,
      private loadingOverlayStore: LoadingOverlayStoreService,
      private router: Router,
      private messageService: MessageService,
      private formTaskService: PhobosTaskInstanceService,
      private destroyRef: DestroyRef
   ) {
      this.propStore.currentProperty
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe((value) => {
            this.selectedProperty = value;
         });
   }

   ngOnInit(): void {
      this.isLoading = true;
      this.mentionStore.mentions
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe((m) => {
            this.mentions = m;
            this.isLoading = false;
            this.isRemovingMentions = false;
         });
   }

   deleteSelected(): void {
      this.isRemovingMentions = true;
      const idArray: number[] = [];
      const formIdArray: number[] = [];

      const loadingState = new LoadingState();
      loadingState.Mentions = true;
      this.loadingOverlayStore.setLoadingState(loadingState);

      for (const i of this.selectedMentions) {
         if (i.type === WorkflowType.Form) formIdArray.push(i.id);
         else idArray.push(i.id);
      }

      this.mentionStore
         .deleteMentions(idArray)
         .pipe(
            switchMap(() => {
               if (formIdArray.length > 0) {
                  return this.formTaskService.completeMention(formIdArray);
               }
               return of(0);
            }),
            finalize(() => {
               this.isRemovingMentions = false;
               this.loadingOverlayStore.setLoadingState(new LoadingState());
               this.mentionStore.refreshStore();
               this.selectedMentions = [];
            })
         )
         .subscribe({
            next: (result) => {
               this.messageService.add({
                  severity: 'success',
                  summary: 'Mentions Deleted',
                  detail: `${result} mention(s) successfully deleted`
               });
            },
            error: () => {
               this.messageService.add({
                  severity: 'error',
                  summary: 'Delete failed',
                  detail: 'Could not remove selected mentions.'
               });
            }
         });
   }

   async goToMention(mention: Mention, $event: MouseEvent) {
      $event.preventDefault();
      await this.redirectToMention(mention);
   }

   async redirectToMention(mention: Mention) {
      if (this.selectedProperty) {
         this.mentionStore.refreshStore();
         await TaskUtils.goToMention(mention, this.router, this.selectedProperty);
      }
   }
}
